import { fetchAccount } from "@/services/account.queries";
import { getPaymentIntent } from "@/services/payment.queries";
import { useToastNotify } from "@/stores/toastStore";
import { isEmail } from "@/utils/etc";
import { getValueFromStorage } from "@/utils/secureStore";
import { useIsFocused } from "@react-navigation/native";
import { useQuery } from "@tanstack/react-query";
import { t } from "@transifex/native";
import { createURL } from "expo-linking";
import { useRouter } from "expo-router";
import { ContactType } from "ipctg-inflow-types/InFlowEnums";
import { useState } from "react";
import { useRefreshOnFocus } from "./useRefreshOnFocus";

/**
 * Only enable with a successURL and if there is a valid email.
 */

interface UsePaymentArgs {
  termsAndConditionsOKTimestamp: string;
  rateID: string;
  rateName: string;
}

export const usePayment = (args: UsePaymentArgs) => {
  const { notify } = useToastNotify();
  const [errorNotified, setErrorNotified] = useState(false);
  const router = useRouter();
  const isFocused = useIsFocused();

  const {
    data: payment,
    error,
    isPending,
    refetch,
  } = useQuery({
    enabled: isFocused,
    queryKey: ["payment-intent", args],
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const isPurchasingPass = await getValueFromStorage("isPurchasingPass");
      const contact = await getValueFromStorage("contact");
      const idToken = await getValueFromStorage("idToken");

      const successURL = createURL(
        `/purchase/confirmation?isPurchasingPass=${isPurchasingPass}&requiresPayment=true&termsAndConditionsOKTimestamp=${args.termsAndConditionsOKTimestamp}&rateID=${args.rateID}`,
      );

      const cancelURL = createURL("/cart", {
        queryParams: {
          isPurchasingPass: isPurchasingPass === "true" ? "true" : "false",
          rateID: args.rateID,
          rateName: args.rateName,
        },
      });

      let email = isEmail(contact) ? contact : null;
      const phone = email ? null : contact;

      if ((!email || !contact) && idToken) {
        const accountResponse = await fetchAccount({ cognitoIDToken: idToken });
        email = accountResponse.email;
      }

      return await getPaymentIntent({
        cognitoIDToken: idToken ?? null,
        successURL,
        cancelURL,
        contactType: isEmail(contact || email) ? ContactType.Email : ContactType.Phone,
        email,
        phone,
      });
    },
  });

  useRefreshOnFocus(refetch);

  if (error && !errorNotified) {
    notify({
      severity: "error",
      message: t("An error occurred when fetching payment data. Please try again."),
    });
    console.error("Error fetching payment data:", error);
    setErrorNotified(true);
    router.replace("/welcome");
  }

  if (!error && errorNotified) {
    setErrorNotified(false);
  }

  return {
    payment,
    isPending,
  };
};
